import { flatten, isObject, merge, pick } from 'lodash';
import {
  PASSWORD_LETTER_PATTERN,
  PASSWORD_NUMBER_PATTERN,
  PASSWORD_SPECIAL_CHARS_PATTERN,
  SET_DATA
} from 'helpers/Constants';
import { postData } from 'helpers/RequestHelper';
import { ruleExecutor } from 'reg_redesign/redux/middleware';
import config from 'reg_redesign/config';
import incomeInfoValidations from 'validations/IncomeInfoValidations';
import moment from 'moment';
import validations from 'validations/validations';

export function validPasswordLength(value) {
  return value.length > 11;
}

export function validPasswordCase(value) {
  return !!value.match(PASSWORD_LETTER_PATTERN);
}

export function validPasswordNumber(value) {
  return !!value.match(PASSWORD_NUMBER_PATTERN);
}

export function validPasswordEmail(value, email) {
  const username = email.split('@')[0].toLowerCase();
  return !value.toLowerCase().includes(username);
}

export function validPasswordSpecialChars(value) {
  return !!value.match(PASSWORD_SPECIAL_CHARS_PATTERN);
}

export function validPasswordsMatch(value, _email, confirmation) {
  return value === confirmation;
}

export async function validPasswordPrevious(
  password,
  confirmation,
  passwordResetToken
) {
  if (password !== confirmation) return true;

  const response = await postData({
    url: '/auth/validate_password',
    body: {
      user: {
        password,
        reset_password_token: passwordResetToken
      }
    }
  });
  return response.data?.valid;
}

const getValidationForField = (page, id) => {
  const pageValidations = pick(
    validations,
    flatten(
      config[page].content.map(
        (field) => field.props.id || field.props.idsToValidate
      )
    )
  );

  return pageValidations[id];
};

export const validateLastPayrollDate = (page, id, state) => {
  if (!getValidationForField(page, id)) return {};

  return incomeInfoValidations.lastPayrollDate(
    'lastPayrollDate',
    state,
    moment().subtract(40, 'day').toDate(),
    moment()
  );
};

export const validateFieldForPage = (page, id, state) => {
  const fieldValidation = getValidationForField(page, id);

  if (!fieldValidation) return {};

  if (fieldValidation) {
    const error = pick(state.errors, [id]);
    const filteredError = error[id] === '' ? {} : error;
    const result = merge(fieldValidation(state), filteredError);

    if (result[id]) {
      return result;
    }
  }

  return {};
};

export const revalidateNextPayrollDate = (page, id, store) => {
  const nextPayrollDateField = config[page].content.find(
    (field) => field.props.id === id
  );
  const minDate = moment().add(
    store?.additionalProps?.nextPayrollDate?.minDate,
    'day'
  );
  const maxDate = moment().add(
    store?.additionalProps?.nextPayrollDate?.maxDate,
    'day'
  );
  if (nextPayrollDateField && minDate && maxDate) {
    return validations.date(id, store, minDate, maxDate);
  }
  return {};
};

// Exported for tests
export function findProperty(collection = {}, result = []) {
  if (
    Object.getOwnPropertyDescriptor(collection, 'property') &&
    !result.includes(collection.property)
  ) {
    result.push(collection.property);
  }
  if (Array.isArray(collection)) {
    collection.forEach((ele) => {
      findProperty(ele, result);
    });
  } else if (isObject(collection)) {
    Object.keys(collection).forEach((objProp) => {
      if (objProp === 'rule') {
        findProperty(collection[objProp], result);
      }
    });
  }
}

export function buildRulesAndTriggers(store, rulesObj = {}) {
  const rules = {};
  const triggers = {};
  // mapping triggers and rules for constant time look up
  Object.keys(rulesObj).forEach((key) => {
    const result = [];
    findProperty(rulesObj[key], result);
    result.sort();
    const propRuleName = result.join('*');
    result.forEach((propertyName) => {
      if (!Array.isArray(triggers[propertyName])) {
        triggers[propertyName] = [propRuleName];
      } else if (!triggers[propertyName].includes(propRuleName)) {
        triggers[propertyName].push(propRuleName);
      }
    });
    if (Array.isArray(rules[propRuleName])) {
      rules[propRuleName].push(...rulesObj[key]);
    } else {
      rules[propRuleName] = [...rulesObj[key]];
    }
  });
  store.dispatch({
    type: SET_DATA,
    payload: {
      rules,
      triggers
    }
  });
}

export const extractPageRule = (store, configObj) => {
  const result = {};
  if (isObject(configObj)) {
    Object.keys(configObj).forEach((page) => {
      result[page] = pick(configObj[page], ['defaultNextPage', 'pageRule']);
    });
  }
  store.dispatch({
    type: SET_DATA,
    payload: {
      pageRules: result
    }
  });
};

export function replaceProps(str, state) {
  let newString = str;
  for (let i = 0; i < str.length; i += 1) {
    if (str.charAt(i) === '{' && str.charAt(i + 1) === '{') {
      const stopIndex = str.indexOf('}}', i);
      const tempProp = str.substring(i + 2, stopIndex);
      newString = newString.replace(`{{${tempProp}}}`, state[tempProp]);
    }
  }
  return newString;
}

export function runDisplayPropsRules(state, dispatch, displayPropsRules) {
  Object.keys(displayPropsRules).forEach((key) => {
    displayPropsRules[key].forEach((rule) => {
      ruleExecutor(rule, state, dispatch);
    });
  });
}

export function initialVisibilitySetup(store, configObj) {
  Object.keys(configObj).forEach((pageKey) => {
    configObj[pageKey].content.forEach((field) => {
      if (field?.props?.id) {
        store.dispatch({
          type: SET_DATA,
          payload: {
            fieldsToDisplay: {
              [field.props.id]:
                field.props.isVisible === undefined
                  ? true
                  : !!field.props.isVisible
            }
          }
        });
      }
    });
  });
}
